import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import './index.css';
import api from './request'; // Import the API instance
import ErrorMessage from './components/ErrorMessage';
const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {

      e.preventDefault();
      
      if (validateForm()) {
        setIsLoading(true);
        
        try {
          const response = await api.post('/login', {
            email: formData.email,
            password: formData.password
          });
        
          // Store token if needed, or just user info
          localStorage.setItem('stepping4life_user', JSON.stringify(response.data.user));
        
          setIsLoading(false);
          navigate('/map');
        } catch (error) {
          console.log(error)
          setIsLoading(false);
          if (error.response?.status === 401) {
            setErrors({ email: 'Invalid credentials' });
          } else {
            setErrors({ global: 'Something went wrong. Please try again.' });
          }
        }


      }

  };

  return (
    <div className="min-h-screen flex flex-col bg-background-light">
      {/* Header */}
      <header className="py-4 px-6 flex justify-center">
        <h1 className="text-2xl font-bold text-primary">Stepping<span className="text-secondary">4</span>Life</h1>
      </header>
      
      {/* Main content */}
      <main className="flex-1 flex flex-col md:flex-row">
        {/* Left side - Welcome banner */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-primary to-secondary text-white p-12 flex-col justify-center items-start">
          <h2 className="text-3xl font-bold mb-6">Welcome Back!</h2>
          <p className="text-lg mb-8">Continue your journey to better health with every step you take.</p>
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="bg-white bg-opacity-20 p-2 rounded-full">
                <ArrowRight className="h-6 w-6" />
              </div>
              <span>Track your daily steps</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-white bg-opacity-20 p-2 rounded-full">
                <ArrowRight className="h-6 w-6" />
              </div>
              <span>Join walking challenges</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-white bg-opacity-20 p-2 rounded-full">
                <ArrowRight className="h-6 w-6" />
              </div>
              <span>Connect with other walkers</span>
            </div>
          </div>
        </div>
        
        {/* Right side - Login form */}
        <div className="w-full md:w-1/2 flex justify-center items-center p-6">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-text-primary">Sign In</h2>
              <p className="text-text-secondary mt-2">Access your Stepping4Life account</p>
            </div>
            <ErrorMessage message={errors.global} />

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={18} className="text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                    placeholder="name@example.com"
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>
              
              {/* Password field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock size={18} className="text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-12 py-2 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff size={18} className="text-gray-400" />
                    ) : (
                      <Eye size={18} className="text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
              </div>
              
              {/* Remember me & Forgot password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-text-secondary">
                    Remember me
                  </label>
                </div>
                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-secondary hover:text-primary">
                    Forgot password?
                  </Link>
                </div>
              </div>
              
              {/* Submit button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
              
              {/* Social login options */}
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-background-light text-text-secondary">Or continue with</span>
                  </div>
                </div>
                
                {/* <div className="mt-6 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-text-primary hover:bg-gray-50"
                  >
                    Google
                  </button>
                  <button
                    type="button"
                    className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-text-primary hover:bg-gray-50"
                  >
                    Apple
                  </button>
                </div> */}
              </div>
            </form>
            
            {/* Sign up link */}
            <div className="text-center mt-8">
              <p className="text-sm text-text-secondary">
                Don't have an account?{' '}
                <Link to="/signup" className="font-medium text-secondary hover:text-primary">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="py-4 px-6 bg-white border-t border-gray-200">
        <div className="text-center text-sm text-text-secondary">
          &copy; {new Date().getFullYear()} Stepping4Life. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Login;